
/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { 
    CreditCard, 
    Lock, 
    Eye, 
    RefreshCw, 
    Smartphone, 
    Shield, 
    Wifi, 
    ShoppingBag, 
    Coffee, 
    Zap, 
    Info, 
    CheckCircle,
    ChevronRight,
    ArrowUpRight
} from 'lucide-react';

export const GhostCardView: React.FC = () => {
    const [isFrozen, setIsFrozen] = useState(false);
    const [showDetails, setShowDetails] = useState(false);
    const GHOST_LOGO_URL = "https://raw.githubusercontent.com/ghost-protocol-labs/ghost-token-sui/refs/heads/main/assets/GHOST.svg";

    return (
        <div className="space-y-8 max-w-6xl mx-auto animate-fade-in pb-12">
            {/* Header Section */}
             <div className="flex flex-col md:flex-row justify-between items-center md:items-end gap-6 text-center md:text-left px-4 sm:px-0">
                <div className="space-y-1">
                   <div className="flex items-center justify-center md:justify-start gap-3 mb-1">
                        <h2 className="font-serif text-3xl sm:text-4xl text-[#0A0B0F] dark:text-white font-bold tracking-tight">Ghost Card</h2>
                        <span className="px-3 py-1 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 text-[10px] font-black uppercase rounded-full border border-emerald-500/20 shadow-sm">Vault Linked</span>
                   </div>
                   <p className="text-stone-500 text-sm max-w-md">
                     Institutional physical metal edition. Forged from aerospace-grade titanium.
                   </p>
                </div>
                <div className="flex gap-2 w-full md:w-auto">
                    <Button variant="outline" size="sm" className="flex-1 md:flex-none h-12 rounded-sm font-black text-[10px]" icon={<RefreshCw size={14}/>}>Rotate CVV</Button>
                    <Button variant="secondary" size="sm" className="flex-1 md:flex-none h-12 rounded-sm font-black text-[10px] bg-[#0A0B0F] text-white dark:bg-white dark:text-[#0A0B0F] shadow-lg" icon={<Smartphone size={14}/>}>Add to Wallet</Button>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-start px-4 sm:px-0">
                {/* 1. Professional Titanium Visual - Ultra High Fidelity */}
                <div className="flex justify-center items-center py-6 sm:py-10">
                    <div className="relative w-full max-w-[460px] aspect-[1.586/1] rounded-md shadow-[0_50px_100px_-20px_rgba(0,0,0,0.5),0_30px_60px_-30px_rgba(0,0,0,0.3)] border border-white/10 p-10 flex flex-col justify-between text-white overflow-hidden group hover:scale-[1.02] transition-all duration-700 select-none">
                         
                         {/* Realistic Brushed Metal Finish */}
                         <div className="absolute inset-0 bg-[#0A0B0F]">
                            {/* Deep base gradient */}
                            <div className="absolute inset-0 bg-gradient-to-br from-[#1A1C23] via-[#0A0B0F] to-[#12141A]"></div>
                            
                            {/* Brushed titanium texture overlay */}
                            <div className="absolute inset-0 opacity-[0.07] bg-[url('https://www.transparenttextures.com/patterns/brushed-alum.png')] mix-blend-overlay"></div>
                            
                            {/* Micro-noise for realism */}
                            <div className="absolute inset-0 opacity-[0.03] bg-[url('https://www.transparenttextures.com/patterns/60-lines.png')]"></div>

                            {/* Subsurface Scattering / Internal Glow */}
                            <div className="absolute -right-20 -bottom-20 w-80 h-80 bg-nobel-gold/10 rounded-full blur-[100px] pointer-events-none group-hover:bg-nobel-gold/20 transition-all duration-1000"></div>
                            
                            {/* Laser-etched Logo watermark */}
                            <div className="absolute -right-12 -bottom-12 w-64 h-64 opacity-[0.02] rotate-12 pointer-events-none mix-blend-plus-lighter">
                                <img src={GHOST_LOGO_URL} alt="" className="w-full h-full object-contain invert" />
                            </div>
                         </div>

                         {/* Moving Light Reflection (Animate on hover) */}
                         <div className="absolute inset-0 z-10 pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-700">
                             <div className="absolute top-[-100%] left-[-100%] w-[300%] h-[300%] bg-[linear-gradient(45deg,transparent_45%,rgba(255,255,255,0.05)_48%,rgba(255,255,255,0.15)_50%,rgba(255,255,255,0.05)_52%,transparent_55%)] animate-shimmer"></div>
                         </div>

                         {/* Top Section: Chip & Network */}
                         <div className="relative z-20 flex justify-between items-start">
                             <div className="space-y-6">
                                {/* High-Fidelity EMV Chip */}
                                <div className="w-14 h-10 rounded-[4px] bg-gradient-to-br from-[#E8C07D] via-[#C5A059] to-[#8A6D3B] shadow-[inset_0_1px_2px_rgba(255,255,255,0.4),0_2px_4px_rgba(0,0,0,0.3)] relative overflow-hidden border border-black/10">
                                     {/* Chip circuitry pattern */}
                                     <div className="absolute inset-0 opacity-40 bg-[repeating-linear-gradient(90deg,transparent,transparent_2px,rgba(0,0,0,0.3)_2px,rgba(0,0,0,0.3)_3px)]"></div>
                                     <div className="absolute inset-0 opacity-40 bg-[repeating-linear-gradient(0deg,transparent,transparent_2px,rgba(0,0,0,0.3)_2px,rgba(0,0,0,0.3)_3px)]"></div>
                                     {/* Center line */}
                                     <div className="w-full h-[0.5px] bg-black/20 absolute top-1/2 -translate-y-1/2"></div>
                                     <div className="h-full w-[0.5px] bg-black/20 absolute left-1/2 -translate-x-1/2"></div>
                                </div>
                                <div className="text-[9px] font-black text-nobel-gold uppercase tracking-[0.4em] drop-shadow-md">AEROSPACE FORGED</div>
                             </div>
                             <div className="flex flex-col items-end gap-5">
                                <div className="p-2 rounded-full bg-white/5 border border-white/5 shadow-inner">
                                    <Wifi className="rotate-90 opacity-40 group-hover:opacity-100 transition-all duration-500" size={20} />
                                </div>
                                <div className="text-[9px] font-black text-stone-500 uppercase tracking-widest bg-black/40 px-3 py-1.5 rounded-sm border border-white/5 backdrop-blur-md">GHOST v2.1 ELITE</div>
                             </div>
                         </div>

                         {/* Bottom Section: Number & Holder */}
                         <div className="relative z-20">
                             <div className="font-mono text-2xl sm:text-3xl tracking-[0.25em] mb-10 flex items-center justify-center gap-6 group/num group-hover:text-white transition-colors">
                                 <span className="drop-shadow-[0_4px_12px_rgba(0,0,0,0.8)] font-bold">
                                    {showDetails ? "4592 1029 3847 9201" : "•••• •••• •••• 9201"}
                                 </span>
                                 <button 
                                    onClick={() => setShowDetails(!showDetails)} 
                                    className="opacity-20 hover:opacity-100 transition-all p-2.5 hover:bg-white/10 rounded-full group-hover/num:opacity-60 bg-white/5 border border-white/5"
                                    aria-label={showDetails ? "Hide Details" : "Show Details"}
                                >
                                     {showDetails ? <Eye size={18} /> : <Lock size={18} />}
                                 </button>
                             </div>
                             
                             <div className="flex justify-between items-end">
                                 <div className="space-y-1.5">
                                     <div className="text-[7px] uppercase tracking-[0.5em] text-stone-500 font-black">Authorized Holder</div>
                                     <div className="font-bold tracking-[0.1em] text-sm uppercase text-white/90 drop-shadow-md">ALEXANDER GHOST</div>
                                 </div>
                                 <div className="flex flex-col items-end">
                                     <div className="italic font-serif font-bold text-3xl text-white/95 leading-none tracking-tighter">Ghost</div>
                                     <div className="text-[8px] font-mono text-nobel-gold/60 tracking-[0.2em] uppercase mt-2 font-bold">Titanium Sovereign</div>
                                 </div>
                             </div>
                         </div>

                         {/* Physical Edge Highlights */}
                         <div className="absolute inset-0 ring-1 ring-inset ring-white/10 rounded-md pointer-events-none"></div>
                         <div className="absolute top-0 left-0 w-full h-[0.5px] bg-white/20 opacity-40"></div>
                         <div className="absolute top-0 left-0 h-full w-[0.5px] bg-white/10 opacity-20"></div>
                    </div>
                </div>

                {/* 2. Control Panel */}
                <div className="space-y-6">
                    <Card className="p-8 border-stone-200 dark:border-white/5 rounded-md bg-white dark:bg-[#15171C] shadow-sm">
                        <div className="flex items-center gap-4 mb-10">
                            <div className="p-3 bg-stone-100 dark:bg-white/5 rounded-xl text-stone-900 dark:text-white shadow-inner">
                                <Shield size={24} />
                            </div>
                            <div>
                                <h3 className="font-bold text-[#0A0B0F] dark:text-white uppercase tracking-widest text-sm">Hardware Enclave Controls</h3>
                                <p className="text-[10px] text-stone-500 font-bold uppercase tracking-widest mt-1">Real-time Node Interaction</p>
                            </div>
                        </div>
                        
                        <div className="space-y-4">
                             <div className={`flex items-center justify-between p-5 rounded-md border transition-all duration-300 ${isFrozen ? 'bg-red-500/10 border-red-500/50' : 'bg-gray-50 dark:bg-white/[0.02] border-stone-100 dark:border-white/10 hover:border-stone-200'}`}>
                                 <div className="flex items-center gap-4">
                                     <div className={`p-3 rounded-lg shadow-sm transition-colors ${isFrozen ? 'bg-red-500 text-white' : 'bg-white dark:bg-white/10 text-stone-400'}`}>
                                        <Lock size={20}/>
                                     </div>
                                     <div>
                                         <div className="text-sm font-black text-[#0A0B0F] dark:text-white uppercase tracking-wider">Digital Killswitch</div>
                                         <div className="text-[10px] text-stone-500 uppercase tracking-widest font-bold mt-1">Instantly lock card access</div>
                                     </div>
                                 </div>
                                 <button 
                                    onClick={() => setIsFrozen(!isFrozen)}
                                    className={`w-14 h-7 rounded-full p-1 transition-all duration-500 ${isFrozen ? 'bg-red-500 shadow-inner' : 'bg-stone-200 dark:bg-stone-800'}`}
                                >
                                    <div className={`w-5 h-5 rounded-full bg-white shadow-2xl transition-transform duration-300 ${isFrozen ? 'translate-x-7' : 'translate-x-0'}`}></div>
                                </button>
                             </div>

                             <div className="flex items-center justify-between p-5 rounded-md border border-stone-100 dark:border-white/10 bg-gray-50 dark:bg-white/[0.02] hover:border-stone-200 transition-all">
                                 <div className="flex items-center gap-4">
                                     <div className="p-3 rounded-lg bg-white dark:bg-white/10 text-stone-400 shadow-sm">
                                        <Smartphone size={20}/>
                                     </div>
                                     <div>
                                         <div className="text-sm font-black text-[#0A0B0F] dark:text-white uppercase tracking-wider">Contactless Rails</div>
                                         <div className="text-[10px] text-stone-500 uppercase tracking-widest font-bold mt-1">Apple & Google Pay Integration</div>
                                     </div>
                                 </div>
                                 <button className="w-14 h-7 rounded-full p-1 bg-emerald-500 shadow-inner">
                                    <div className="w-5 h-5 rounded-full bg-white shadow-2xl translate-x-7"></div>
                                </button>
                             </div>

                             <div className="flex items-center justify-between p-5 rounded-md border border-stone-100 dark:border-white/10 bg-gray-50 dark:bg-white/[0.02] hover:border-stone-200 transition-all">
                                 <div className="flex items-center gap-4">
                                     <div className="p-3 rounded-lg bg-white dark:bg-white/10 text-stone-400 shadow-sm">
                                        <Zap size={20}/>
                                     </div>
                                     <div>
                                         <div className="text-sm font-black text-[#0A0B0F] dark:text-white uppercase tracking-wider">Dynamic Spend Limit</div>
                                         <div className="text-[10px] text-stone-500 uppercase tracking-widest font-bold mt-1">Current: $5,000 / Day</div>
                                     </div>
                                 </div>
                                 <Button variant="ghost" size="sm" className="h-10 px-4 text-[10px] font-black uppercase tracking-widest">Adjust</Button>
                             </div>
                        </div>
                    </Card>

                    <div className="p-6 bg-nobel-gold/5 dark:bg-nobel-gold/[0.03] rounded-md border border-dashed border-nobel-gold/20 flex items-start gap-5 shadow-inner">
                        <div className="w-12 h-12 rounded-xl bg-nobel-gold/10 flex items-center justify-center shrink-0 border border-nobel-gold/10 shadow-sm">
                            <Info size={24} className="text-nobel-gold" />
                        </div>
                        <div className="text-xs text-stone-500 dark:text-stone-400 leading-relaxed pt-1">
                            <strong className="text-[#0A0B0F] dark:text-white uppercase tracking-widest block mb-1">Production Status Update</strong> 
                            Your solid titanium hardware edition is currently in the <span className="text-nobel-gold font-bold">laser-etching</span> phase. Shipping telemetry will be provisioned in <span className="font-bold text-[#0A0B0F] dark:text-white">Q4 2026</span>.
                        </div>
                    </div>
                </div>
            </div>

            {/* 3. Spend Ledger */}
            <div className="px-4 sm:px-0">
                <Card noPadding className="overflow-hidden border-stone-200 dark:border-white/10 shadow-xl rounded-md transition-colors duration-300">
                    <div className="flex flex-col sm:flex-row justify-between items-center p-8 border-b border-stone-100 dark:border-white/5 bg-gray-50/50 dark:bg-white/[0.02] gap-6 text-center sm:text-left">
                        <div>
                            <h3 className="font-serif text-2xl font-bold text-[#0A0B0F] dark:text-white tracking-tight">Sovereign Spend Ledger</h3>
                            <p className="text-[10px] text-stone-400 uppercase font-black tracking-[0.25em] mt-1">End-to-End Encrypted Ledger Data</p>
                        </div>
                        <div className="flex gap-3">
                            <Button variant="outline" size="sm" className="rounded-sm font-black text-[10px] h-11 px-6 uppercase tracking-widest">Export CSV</Button>
                            <Button variant="secondary" size="sm" className="rounded-sm font-black text-[10px] h-11 px-6 bg-[#0A0B0F] text-white dark:bg-white dark:text-[#0A0B0F] uppercase tracking-widest shadow-lg">Verify Proofs</Button>
                        </div>
                    </div>
                    <div className="divide-y divide-stone-100 dark:divide-white/5 max-h-[500px] overflow-y-auto custom-scrollbar">
                        {[
                            { name: "Suiet Mall", date: "Today, 10:42 AM", amount: "-$1,099.00", icon: ShoppingBag, category: "Luxury Retail", status: "Confirmed", network: "SUI" },
                            { name: "Ghost Coffee", date: "Yesterday, 8:15 AM", amount: "-$12.50", icon: Coffee, category: "Hospitality", status: "Confirmed", network: "SOL" },
                            { name: "Global Node Hosting", date: "Nov 20, 4:30 PM", amount: "-$32.00", icon: Zap, category: "Infrastructure", status: "Confirmed", network: "SOL" },
                            { name: "DeFi Swap Router", date: "Nov 18, 11:15 AM", amount: "-$450.00", icon: ArrowUpRight, category: "Treasury", status: "Confirmed", network: "SUI" },
                        ].map((tx, i) => (
                            <div key={i} className="flex flex-col sm:flex-row items-center justify-between p-6 hover:bg-stone-50 dark:hover:bg-white/[0.02] transition-all group cursor-pointer border-l-4 border-transparent hover:border-l-nobel-gold gap-4 sm:gap-0">
                                <div className="flex items-center gap-5 w-full sm:w-auto">
                                    <div className="w-14 h-14 rounded-md bg-stone-100 dark:bg-white/5 border border-stone-200 dark:border-white/10 flex items-center justify-center text-stone-600 dark:text-stone-300 group-hover:scale-105 transition-transform shadow-inner group-hover:shadow-lg shrink-0">
                                        <tx.icon size={26} />
                                    </div>
                                    <div className="min-w-0">
                                        <div className="font-bold text-[#0A0B0F] dark:text-white text-base leading-none mb-1.5">{tx.name}</div>
                                        <div className="text-[10px] text-stone-400 font-bold uppercase tracking-widest">{tx.date} • {tx.category}</div>
                                    </div>
                                </div>
                                <div className="flex items-center justify-between sm:justify-end w-full sm:w-auto gap-8">
                                    <div className="flex items-center gap-2 px-3 py-1 bg-stone-100 dark:bg-white/5 rounded-md border border-stone-200 dark:border-white/10">
                                        <span className="text-[9px] font-mono font-bold text-stone-500">{tx.network} Network</span>
                                    </div>
                                    <div className="text-right">
                                        <div className="font-mono font-black text-stone-900 dark:text-white text-lg leading-none">
                                            {tx.amount}
                                        </div>
                                        <div className="text-[9px] font-black text-emerald-500 uppercase tracking-widest flex items-center justify-end gap-1.5 mt-1.5">
                                            <CheckCircle size={10} strokeWidth={3} /> {tx.status}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </Card>
            </div>

            <style>
                {`
                @keyframes shimmer {
                    from { transform: translate(-50%, -50%) rotate(0deg); }
                    to { transform: translate(-50%, -50%) rotate(360deg); }
                }
                .animate-shimmer {
                    animation: shimmer 15s linear infinite;
                }
                `}
            </style>
        </div>
    );
};
